import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { invalidateTaskQueries } from "@/lib/task-query-utils";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClipboardList, Calendar, PawPrint, Link2 } from "lucide-react";
import { PriorityBadge } from "@/components/tasks/priority-badge";

interface TaskLabel {
  labelId: string;
  labelName: string;
  labelColor: string | null;
}

interface MyTask {
  id: string;
  title: string;
  boardId: string;
  boardName: string;
  statusId: string | null;
  statusName: string | null;
  dueDate: string | null;
  priority: string | null;
  source: string | null;
  createdAt: string;
  animalCount: number;
  firstAnimalName: string | null;
  interactionCount: number;
  labels: TaskLabel[];
}

interface BoardStatus {
  id: string;
  name: string;
  isArchived: boolean;
  sortOrder: number;
}

interface MyTasksWidgetProps {
  maxItems?: number;
  showBoard?: boolean;
  onTaskClick?: (taskId: string) => void;
}

function isOverdue(dueDate: string | null): boolean {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dueDate) < today;
}

function isDone(statusName: string | null): boolean {
  return statusName?.toLowerCase() === "done";
}

function StatusSelectWithFetch({ task, onUpdate }: {
  task: MyTask;
  onUpdate: (taskId: string, statusId: string) => void;
}) {
  const { data: statusesData } = useQuery<{ statuses: BoardStatus[] }>({
    queryKey: ["/api/task-boards", task.boardId, "statuses"],
  });
  const statuses = (statusesData?.statuses ?? []).filter(s => !s.isArchived);

  if (statuses.length > 0) {
    return (
      <Select
        value={task.statusId ?? ""}
        onValueChange={(val) => onUpdate(task.id, val)}
      >
        <SelectTrigger className="h-7 text-xs w-full" data-testid={`task-my-status-select-${task.id}`}>
          <SelectValue placeholder="Set status" />
        </SelectTrigger>
        <SelectContent>
          {statuses.map(s => (
            <SelectItem key={s.id} value={s.id} data-testid={`task-my-status-option-${task.id}-${s.id}`}>
              {s.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (task.statusName) {
    return <Badge variant="secondary" className="text-xs" data-testid={`task-my-status-${task.id}`}>{task.statusName}</Badge>;
  }
  return <span className="text-xs text-muted-foreground">—</span>;
}

export function MyTasksWidget({ maxItems = 20, showBoard = true, onTaskClick }: MyTasksWidgetProps) {
  const [, setLocation] = useLocation();

  function handleTaskClick(taskId: string) {
    if (onTaskClick) {
      onTaskClick(taskId);
    } else {
      setLocation(`/tasks/${taskId}`);
    }
  }
  const { toast } = useToast();

  const { data, isLoading } = useQuery<{ tasks: MyTask[] }>({
    queryKey: ["/api/tasks/my-tasks"],
  });

  const tasks = [...(data?.tasks ?? [])]
    .sort((a, b) => {
      const aDone = isDone(a.statusName) ? 1 : 0;
      const bDone = isDone(b.statusName) ? 1 : 0;
      return aDone - bDone;
    })
    .slice(0, maxItems);

  const statusMutation = useMutation({
    mutationFn: async ({ taskId, statusId }: { taskId: string; statusId: string }) => {
      return await apiRequest("PATCH", `/api/tasks/${taskId}`, { statusId });
    },
    onSuccess: () => {
      invalidateTaskQueries();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function handleStatusUpdate(taskId: string, statusId: string) {
    statusMutation.mutate({ taskId, statusId });
  }

  return (
    <Card data-testid="task-my-tasks-widget">
      <CardContent className="pt-6 space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-muted-foreground" />
            <h3 className="text-base font-semibold" data-testid="task-my-tasks-title">My Tasks</h3>
          </div>
          <p className="text-xs text-muted-foreground mt-1 ml-7">Tasks assigned to me</p>
        </div>

        {isLoading ? (
          <div className="space-y-3" data-testid="task-my-tasks-loading">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-6" data-testid="task-my-tasks-empty">
            <ClipboardList className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No tasks assigned to you</p>
            <p className="text-xs text-muted-foreground mt-1">Select a board above to browse tasks</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-zebra" data-testid="task-my-tasks-table">
              <thead>
                <tr className="border-b text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="text-left py-2 px-2 font-medium">Task</th>
                  <th className="text-left py-2 px-2 font-medium w-[130px]">Status</th>
                  <th className="text-left py-2 px-2 font-medium w-[90px]">Priority</th>
                  <th className="text-left py-2 px-2 font-medium w-[140px]">Linked</th>
                  <th className="text-left py-2 px-2 font-medium w-[100px]">Due</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(task => {
                  const done = isDone(task.statusName);
                  return (
                    <tr
                      key={task.id}
                      className={`border-b last:border-b-0 cursor-pointer transition-colors hover:bg-muted/50 ${done ? "opacity-50" : ""}`}
                      data-testid={`task-my-row-${task.id}`}
                    >
                      <td
                        className="py-2.5 px-2 min-w-0"
                        onClick={() => handleTaskClick(task.id)}
                      >
                        <p className={`font-medium truncate max-w-[300px] ${done ? "line-through" : ""}`} data-testid={`task-my-title-${task.id}`}>{task.title}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                          {showBoard && (
                            <span className="text-xs text-muted-foreground" data-testid={`task-my-board-${task.id}`}>{task.boardName}</span>
                          )}
                          {task.labels.length > 0 && (
                            <>
                              {showBoard && <span className="text-muted-foreground/40">·</span>}
                              {task.labels.map(l => (
                                <Badge
                                  key={l.labelId}
                                  variant="outline"
                                  className="text-[10px] px-1.5 py-0"
                                  style={l.labelColor ? { borderColor: l.labelColor, color: l.labelColor } : undefined}
                                  data-testid={`task-my-label-${task.id}-${l.labelId}`}
                                >
                                  {l.labelName}
                                </Badge>
                              ))}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 px-2 align-top" onClick={(e) => e.stopPropagation()}>
                        <StatusSelectWithFetch task={task} onUpdate={handleStatusUpdate} />
                      </td>
                      <td
                        className="py-2.5 px-2 align-top"
                        onClick={() => handleTaskClick(task.id)}
                      >
                        {task.priority ? (
                          <PriorityBadge priority={task.priority} className="text-[10px]" />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td
                        className="py-2.5 px-2 align-top"
                        onClick={() => handleTaskClick(task.id)}
                      >
                        <div className="flex flex-col gap-0.5">
                          {task.animalCount > 0 ? (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`task-my-animals-${task.id}`}>
                              <PawPrint className="w-3 h-3 shrink-0" />
                              <span className="truncate max-w-[100px]">{task.firstAnimalName || "Animal"}</span>
                              {task.animalCount > 1 && <span>+{task.animalCount - 1}</span>}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                          {task.interactionCount > 0 && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`task-my-interactions-${task.id}`}>
                              <Link2 className="w-3 h-3 shrink-0" />
                              {task.interactionCount}
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className="py-2.5 px-2 align-top"
                        onClick={() => handleTaskClick(task.id)}
                      >
                        {task.dueDate ? (
                          <span
                            className={`flex items-center gap-1 text-xs whitespace-nowrap ${isOverdue(task.dueDate) ? "text-destructive font-medium" : "text-muted-foreground"}`}
                            data-testid={`task-my-due-${task.id}`}
                          >
                            <Calendar className="w-3 h-3 shrink-0" />
                            {new Date(task.dueDate + "T00:00:00").toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
